import { useRef, useState } from 'react';
import { Funnel, Loader2, Search, X } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useInfiniteSales } from '@/hooks/use-sales';
import {
  countAdvancedSalesFilters,
  flattenSalesPages,
} from '@/lib/sales-history';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PeriodFilter } from '@/components/period/period-filter';
import {
  SaleCard,
  type SalesHistorySale,
} from '@/components/sales-history/sale-card';
import { SaleDetailSheet } from '@/components/sales-history/sale-detail-sheet';
import { SalesFiltersSheet } from '@/components/sales-history/sales-filters-sheet';
import {
  type SalesHistoryFilters,
  useSalesPredefinedDateRanges,
} from '@/components/sales-history/use-sales-history-filters';

/**
 * Mobile sales history (SALES-07 AC 2): inline search, period chips,
 * "Filtros avançados" sheet, order cards with "Carregar mais" (pages of 20)
 * and the detail sheet on tap.
 */
export function SalesHistoryList({
  filters,
}: {
  filters: SalesHistoryFilters;
}) {
  const { LL } = useI18nContext();
  const predefinedDateRanges = useSalesPredefinedDateRanges();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState<{
    sale: SalesHistorySale | null;
    open: boolean;
  }>({ sale: null, open: false });
  const searchRef = useRef<HTMLInputElement>(null);

  const query = useInfiniteSales(filters.values, true);
  const sales = flattenSalesPages(query.data?.pages ?? []);
  const advancedCount = countAdvancedSalesFilters(filters.values);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          ref={searchRef}
          value={filters.textFilter}
          onChange={(event) => filters.setTextFilter(event.target.value)}
          placeholder={LL.salesPage.filters.search()}
          className={cn('px-8', mobileLayer.input)}
        />
        {filters.textFilter && (
          <button
            type="button"
            aria-label={LL.salesPage.filters.cleanSearch()}
            onClick={() => {
              filters.setTextFilter('');
              searchRef.current?.focus();
            }}
            className="absolute right-0 top-0 flex h-full min-w-touch items-center justify-center text-muted-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        )}
      </div>

      <PeriodFilter
        fromDate={filters.fromDateFilter}
        setFromDate={filters.setFromDateFilter}
        toDate={filters.toDateFilter}
        setToDate={filters.setToDateFilter}
        predefinedDateRange={filters.predefinedDateRange}
        setPredefinedDateRange={filters.setPredefinedDateRange}
        predefinedDateRanges={predefinedDateRanges}
        defaultDateRange="since_beginning"
      />

      <Button
        type="button"
        variant="outline"
        className={cn('self-start', mobileLayer.touch)}
        onClick={() => setFiltersOpen(true)}
      >
        <Funnel className="size-4" aria-hidden />
        {LL.salesPage.filters.advancedFilters()}
        {advancedCount > 0 && (
          <Badge className="ml-1 px-1.5 tabular-nums">{advancedCount}</Badge>
        )}
      </Button>

      {query.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-mobile-card" />
          ))}
        </div>
      ) : query.isError && sales.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {LL.salesPage.failedToLoadSalesData()}
          </p>
          <Button
            type="button"
            variant="outline"
            className={mobileLayer.touch}
            onClick={() => query.refetch()}
          >
            {LL.salesPage.mobile.retry()}
          </Button>
        </div>
      ) : sales.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {LL.salesPage.mobile.empty()}
          </p>
          {filters.hasActiveFilters && (
            <Button
              type="button"
              variant="outline"
              className={mobileLayer.touch}
              onClick={filters.clearFilters}
            >
              {LL.salesPage.mobile.clearFilters()}
            </Button>
          )}
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {sales.map((sale) => (
              <li key={sale.id}>
                <SaleCard
                  sale={sale}
                  onOpen={() => setDetail({ sale, open: true })}
                />
              </li>
            ))}
          </ul>
          {query.hasNextPage && (
            <Button
              type="button"
              variant="outline"
              className={mobileLayer.touch}
              disabled={query.isFetchingNextPage}
              onClick={() => query.fetchNextPage()}
            >
              {query.isFetchingNextPage && (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              )}
              {/* A failed "Carregar mais" keeps the loaded orders. */}
              {query.isFetchNextPageError
                ? LL.salesPage.mobile.retry()
                : LL.salesPage.mobile.loadMore()}
            </Button>
          )}
        </>
      )}

      <SalesFiltersSheet
        filters={filters}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
      />
      <SaleDetailSheet
        sale={detail.sale}
        open={detail.open}
        onOpenChange={(open) => setDetail((d) => ({ ...d, open }))}
      />
    </div>
  );
}
